import { useRef, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useToast } from './Toast';
import { updateProfile, changePassword } from '../api/auth';
import { roleName } from '../utils/format';
import { fileToAvatarDataUrl } from '../utils/image';
import { passwordError, PASSWORD_HINT } from '../utils/password';
import Avatar from './Avatar';

const isValidPhone = (p) => /^\d{10}$/.test(String(p || ''));
const isValidEmail = (e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(e || ''));

export default function ProfileModal({ onClose }) {
  const { me, setMe } = useAuth();
  const toast = useToast();
  const fileRef = useRef(null);

  // GĐ/CV tự sửa họ tên; ĐỐI TÁC (DT) cũng được vì là người NGOÀI công ty (tên không khớp SocSon).
  const canEditName = me.role === 'GD' || me.role === 'CV' || me.role === 'DT';
  const [avatar, setAvatar] = useState(me.avatar || '');
  const [name, setName] = useState(me.name || '');
  const [email, setEmail] = useState(me.email || '');
  const [phone, setPhone] = useState(me.phone || '');
  const [savingInfo, setSavingInfo] = useState(false);

  const [curPass, setCurPass] = useState('');
  const [newPass, setNewPass] = useState('');
  const [newPass2, setNewPass2] = useState('');
  const [savingPass, setSavingPass] = useState(false);

  async function pickAvatar(e) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    try {
      const dataUrl = await fileToAvatarDataUrl(file);
      setAvatar(dataUrl);
    } catch {
      toast('Không đọc được ảnh', 'danger');
    }
  }

  async function saveInfo() {
    if (savingInfo) return;
    if (email && !isValidEmail(email)) return toast('Email không hợp lệ', 'warn');
    if (phone && !isValidPhone(phone)) return toast('Số điện thoại phải gồm 10 chữ số', 'warn');
    if (!email.trim() && !phone.trim()) return toast('Nhập email hoặc số điện thoại để đăng nhập', 'warn');
    if (canEditName && !name.trim()) return toast('Họ tên không được để trống', 'warn');
    setSavingInfo(true);
    try {
      const res = await updateProfile({ avatar, email: email.trim(), phone: phone.trim(), ...(canEditName ? { name: name.trim() } : {}) });
      setMe(res.me);
      toast('Đã cập nhật thông tin cá nhân');
    } catch (err) {
      toast(err.response?.data?.message || 'Có lỗi xảy ra', 'danger');
    } finally {
      setSavingInfo(false);
    }
  }

  async function savePass() {
    if (savingPass) return;
    if (!curPass || !newPass) return toast('Nhập mật khẩu hiện tại và mật khẩu mới', 'warn');
    const perr = passwordError(newPass);
    if (perr) return toast('Mật khẩu: ' + perr, 'warn');
    if (newPass !== newPass2) return toast('Mật khẩu mới nhập lại không khớp', 'warn');
    setSavingPass(true);
    try {
      await changePassword({ currentPassword: curPass, newPassword: newPass });
      setCurPass('');
      setNewPass('');
      setNewPass2('');
      toast('Đã đổi mật khẩu');
    } catch (err) {
      toast(err.response?.data?.message || 'Có lỗi xảy ra', 'danger');
    } finally {
      setSavingPass(false);
    }
  }

  return (
    <>
      <span className="close" onClick={onClose}>
        ×
      </span>
      <h3>Thông tin tài khoản</h3>

      {/* ----- Thông tin cố định (không sửa được) ----- */}
      <div className="row" style={{ gap: 14, flexWrap: 'wrap', marginBottom: 12 }}>
        <span className="small">
          Cấp bậc: <b>{roleName(me.displayRole || me.role)}</b>
        </span>
        {(me.departments || []).length > 1 && (
          <span className="small">
            {(me.departments || []).slice(1).map((d) => (
              <span key={d} className="tag" style={{ marginRight: 6, background: 'color-mix(in srgb, var(--accent) 16%, transparent)', color: 'var(--accent)', fontSize: 11 }}>CTV {String(d).replace(/^Phòng\s+/i, '')}</span>
            ))}
          </span>
        )}
        {/* Đối tác (DT) là người NGOÀI công ty -> không có nhóm/mã nhân viên, ẩn 2 dòng này. */}
        {me.role !== 'DT' && (
          <>
            <span className="small">
              Nhóm: <b>{me.group ? me.group.name : 'Ban giám đốc'}</b>
            </span>
            <span className="small">
              Mã nhân viên: <b style={{ color: 'var(--warn)' }}>{me.employeeCode || '—'}</b>{' '}
              <span className="muted">(không đổi được)</span>
            </span>
          </>
        )}
      </div>

      {/* ----- Đổi thông tin cá nhân ----- */}
      <div className="card" style={{ background: 'var(--panel2)' }}>
        <h3 style={{ marginBottom: 12 }}>Thông tin cá nhân</h3>

        {/* Ảnh đại diện */}
        <div className="row" style={{ gap: 14, alignItems: 'center', marginBottom: 14 }}>
          <Avatar src={avatar} name={me.name} size={64} />
          <div>
            <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={pickAvatar} />
            <button className="btn ghost sm" onClick={() => fileRef.current?.click()}>📷 Đổi ảnh đại diện</button>
            {avatar && (
              <button className="btn ghost sm" style={{ marginLeft: 8 }} onClick={() => setAvatar('')}>Xóa ảnh</button>
            )}
            <div className="small muted" style={{ marginTop: 4 }}>Ảnh vuông, tự thu nhỏ. Nhớ bấm “Lưu thông tin”.</div>
          </div>
        </div>

        <div className="field">
          <label>Họ tên {canEditName ? null : <span className="muted">(không đổi được)</span>}</label>
          {canEditName ? (
            <input value={name} onChange={(e) => setName(e.target.value)} autoComplete="off" placeholder="Họ tên" />
          ) : (
            <input value={me.name || ''} disabled readOnly />
          )}
        </div>
        <div className="two-col">
          <div className="field">
            <label>Email (đăng nhập)</label>
            <input value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="off" placeholder="vd: ban@congty.com"
              style={email && !isValidEmail(email) ? { borderColor: 'var(--danger)' } : undefined} />
          </div>
          <div className="field">
            <label>Số điện thoại (đăng nhập)</label>
            <input value={phone} onChange={(e) => setPhone(e.target.value)} autoComplete="off" placeholder="10 chữ số"
              style={phone && !isValidPhone(phone) ? { borderColor: 'var(--danger)' } : undefined} />
          </div>
        </div>
        <div className="small muted" style={{ marginTop: 2 }}>Đăng nhập bằng email hoặc số điện thoại này.</div>
        <div className="row" style={{ marginTop: 8 }}>
          <div className="spacer"></div>
          <button className="btn" onClick={saveInfo} disabled={savingInfo}>
            {savingInfo ? 'Đang lưu…' : 'Lưu thông tin'}
          </button>
        </div>
      </div>

      {/* ----- Đổi mật khẩu ----- */}
      <div className="card" style={{ background: 'var(--panel2)' }}>
        <h3 style={{ marginBottom: 4 }}>Đổi mật khẩu</h3>
        <div className="small muted" style={{ marginBottom: 12 }}>{PASSWORD_HINT}</div>
        <div className="field">
          <label>Mật khẩu hiện tại</label>
          <input type="password" value={curPass} onChange={(e) => setCurPass(e.target.value)} autoComplete="off" />
        </div>
        <div className="two-col">
          <div className="field">
            <label>Mật khẩu mới</label>
            <input type="password" value={newPass} onChange={(e) => setNewPass(e.target.value)} autoComplete="off" />
          </div>
          <div className="field">
            <label>Nhập lại mật khẩu mới</label>
            <input type="password" value={newPass2} onChange={(e) => setNewPass2(e.target.value)} autoComplete="off" />
          </div>
        </div>
        <div className="row" style={{ marginTop: 4 }}>
          <div className="spacer"></div>
          <button className="btn ok" onClick={savePass} disabled={savingPass}>
            {savingPass ? 'Đang đổi…' : 'Đổi mật khẩu'}
          </button>
        </div>
      </div>

      <div className="row" style={{ marginTop: 8 }}>
        <div className="spacer"></div>
        <button className="btn ghost" onClick={onClose}>
          Đóng
        </button>
      </div>
    </>
  );
}
